/**
 * Eine Zeile der Mitarbeiterliste: Name, Rolle, Status, Backoffice, App.
 *
 * ═══ ZWEI SPALTEN, ZWEI WERKZEUGE (HJ-010) ═══
 *
 * Früher hießen die Spalten „Zugang" und „Einrichtung", und der Status
 * mischte mit („Angelegt", „Eingeladen"). Aus dem Büro kam: Wann muss ich was
 * klicken, und warum? Jetzt gilt ein Weg je Werkzeug, und jedes hat seine
 * Spalte: „Backoffice" (E-Mail und Passwort, nur Büro und Admin) und „App"
 * (Einrichtungscode, für alle). Jede zeigt ihren Zustand und daneben genau
 * den Knopf, der jetzt dran ist — und darunter in einem Satz, wofür er ist
 * oder warum er gesperrt ist. Sichtbar, nicht als title: einen Tooltip findet
 * nur, wer schon weiß, dass es ihn gibt.
 *
 * Rolle und Status bedient nur ein Admin, und beides geht über die
 * Datenbankfunktion mit Protokoll (HJ-015). Für alle anderen sind die Felder
 * gesperrt — Höflichkeit, die Sperre sitzt in der Datenbank.
 */

import { useState } from 'react';
import { Knopf, Marker, Rueckfrage } from '../gestaltung/bausteine';
import {
  ROLLENTEXT,
  STATUSTEXT,
  type Mitarbeiterzeile,
  type Rolle,
  type Status,
} from '../daten/mitarbeiter';

export function MitarbeiterZeile(eigenschaften: {
  person: Mitarbeiterzeile;
  istAdmin: boolean;
  beschaeftigt: string | null;
  codeErzeugen: () => void;
  codeWeg: () => void;
  zugangOeffnen: (art: 'anlegen' | 'passwort_neu') => void;
  rolleStatusSetzen: (rolle: Rolle, status: Status) => void;
}) {
  const { person, istAdmin } = eigenschaften;
  const laeuft = eigenschaften.beschaeftigt?.endsWith(person.id) ?? false;

  return (
    <tr>
      <td>
        <strong>
          {person.vorname} {person.nachname}
        </strong>
        {person.personalnummer && (
          <div className="meta">Personalnummer {person.personalnummer}</div>
        )}
        {person.ist_testkonto && (
          <div style={{ marginTop: 4 }}>
            <Marker art="warn">Testkonto</Marker>
          </div>
        )}
      </td>

      <td>
        <select
          aria-label="Rolle"
          value={person.rolle}
          disabled={laeuft || !istAdmin}
          onChange={(e) =>
            eigenschaften.rolleStatusSetzen(e.target.value as Rolle, person.status)
          }
        >
          {(Object.keys(ROLLENTEXT) as Rolle[]).map((r) => (
            <option key={r} value={r}>
              {ROLLENTEXT[r]}
            </option>
          ))}
        </select>
      </td>

      <td>
        <select
          aria-label="Status"
          value={person.status}
          disabled={laeuft || !istAdmin}
          onChange={(e) =>
            eigenschaften.rolleStatusSetzen(person.rolle, e.target.value as Status)
          }
        >
          {(Object.keys(STATUSTEXT) as Status[]).map((s) => (
            <option key={s} value={s}>
              {STATUSTEXT[s]}
            </option>
          ))}
        </select>
      </td>

      <td>
        <BackofficeZelle {...eigenschaften} laeuft={laeuft} />
      </td>

      <td>
        <AppZelle {...eigenschaften} laeuft={laeuft} />
      </td>
    </tr>
  );
}

/** Ein Knopf mit seinem Satz darunter — das Paar, aus dem beide Spalten bestehen. */
function KnopfMitHinweis(eigenschaften: {
  text: string;
  hinweis: string;
  art?: 'zweit' | 'still';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <div>
      <Knopf
        art={eigenschaften.art ?? 'zweit'}
        klein
        disabled={eigenschaften.disabled}
        onClick={eigenschaften.onClick}
      >
        {eigenschaften.text}
      </Knopf>
      <span className="knopfhinweis">{eigenschaften.hinweis}</span>
    </div>
  );
}

/* ------------------------------------------------------------- Backoffice */

function BackofficeZelle(eigenschaften: {
  person: Mitarbeiterzeile;
  istAdmin: boolean;
  laeuft: boolean;
  zugangOeffnen: (art: 'anlegen' | 'passwort_neu') => void;
}) {
  const { person, istAdmin } = eigenschaften;

  // Fahrer arbeiten nur mit der App. Ein Knopf hier wäre eine Einladung zu
  // genau dem Fehler, den `code-einloesen` abweist: Bürozugang an einer
  // Fahrerrolle.
  if (person.rolle === 'fahrer') {
    return <span className="meta">Nur App</span>;
  }

  const hatKonto = person.auth_user_id !== null;
  const ausgeschieden = person.status === 'ausgetreten';

  return (
    <div className="zugangszelle">
      {hatKonto ? (
        <div>
          <Marker art="gut">Eingerichtet</Marker>
          {person.email && <div className="meta">{person.email}</div>}
        </div>
      ) : (
        <Marker art="neutral">Kein Zugang</Marker>
      )}
      <KnopfMitHinweis
        text={hatKonto ? 'Neues Passwort vergeben' : 'Backoffice-Zugang anlegen'}
        disabled={eigenschaften.laeuft || !istAdmin || ausgeschieden}
        onClick={() => eigenschaften.zugangOeffnen(hatKonto ? 'passwort_neu' : 'anlegen')}
        hinweis={
          !istAdmin
            ? 'Vergibt nur ein Admin.'
            : ausgeschieden
              ? 'Ausgeschieden — kein Zugang mehr.'
              : hatKonto
                ? 'Bei vergessenem Passwort. Das alte gilt danach nicht mehr.'
                : 'E-Mail und Einmalpasswort für die Anmeldung im Büro.'
        }
      />
    </div>
  );
}

/* -------------------------------------------------------------------- App */

function AppZelle(eigenschaften: {
  person: Mitarbeiterzeile;
  istAdmin: boolean;
  laeuft: boolean;
  codeErzeugen: () => void;
  codeWeg: () => void;
}) {
  const { person, laeuft } = eigenschaften;
  const [fragt, setFragt] = useState(false);
  const istBuero = person.rolle === 'buero' || person.rolle === 'admin';
  const hatKonto = person.auth_user_id !== null;
  // Bei Fahrern IST das Konto das Gerätekonto. Bei Bürorollen ist es der
  // Bürozugang — dort sagt nur ein eingelöster Code, dass die App läuft.
  const eingerichtet = istBuero ? person.appEingerichtetAm !== null : hatKonto;
  const offen = person.offenerCode && !person.codeAbgelaufen;

  /* ═══ ERST DER BÜROZUGANG, DANN DER CODE ═══
   *
   * Andersherum entstünde an einer Bürorolle ein Gerätekonto, und der später
   * angelegte Bürozugang würde es beim Verknüpfen überschreiben. Die
   * Datenbank lehnt das ab (`einrichtungscode_erzeugen`); hier ist der Knopf
   * gesperrt und sagt darunter, warum. Ebenso für alle, die nicht aktiv sind
   * — einen Code für Gesperrte erzeugt die Datenbank nicht mehr (HJ-015).
   *
   * Und nur ein Admin richtet die App für Büro und Admin ein (HJ-015b): Der
   * Code öffnet deren Bürozugang — das ist wie ein Passwort für sie zu setzen.
   */
  const sperrgrund =
    person.status !== 'aktiv'
      ? 'Nur für aktive Mitarbeiter.'
      : istBuero && !eigenschaften.istAdmin
        ? 'Für Büro und Admin richtet das nur ein Admin ein.'
        : istBuero && !hatKonto
          ? 'Erst den Backoffice-Zugang anlegen.'
          : null;

  return (
    <div className="zugangszelle">
      <div>
        {eingerichtet ? (
          <Marker art="gut">App eingerichtet</Marker>
        ) : offen ? (
          <Marker art="warn">Code offen</Marker>
        ) : person.codeAbgelaufen ? (
          <Marker art="schlecht">Code abgelaufen</Marker>
        ) : (
          <Marker art="neutral">Nicht eingerichtet</Marker>
        )}
        {/* Ein offener Code gehört auch neben „eingerichtet" sichtbar gemacht
            — sonst erzeugt jemand einen zweiten, und der erste wird dabei
            stillschweigend verworfen. */}
        {eingerichtet && offen && <div className="meta">Neuer Code offen</div>}
        {person.offenerCode && person.offenerCode.fehlversuche > 0 && (
          <div className="meta">
            {person.offenerCode.fehlversuche === 1
              ? '1 Fehlversuch'
              : `${person.offenerCode.fehlversuche} Fehlversuche`}
          </div>
        )}
      </div>

      <KnopfMitHinweis
        text={eingerichtet ? 'App neu einrichten' : 'App einrichten'}
        // Bei offener Frage NICHT sperren: Ein gesperrter Knopf verliert den
        // Fokus, und dorthin soll er nach der Frage zurück.
        disabled={laeuft || sperrgrund !== null}
        // ═══ RÜCKFRAGE NUR, WENN SCHON EIN HANDY LÄUFT (HJ-026) ═══
        //
        // Testlauf 24.10.: Ein Fehlklick in der falschen Zeile erzeugte sofort
        // einen Code. Löst ihn jemand auf einem ANDEREN Handy ein, bleibt auf
        // dem bisherigen liegen, was dort noch nicht übertragen ist. Bei der
        // Ersteinrichtung gibt es nichts zu verlieren — dort wird nicht
        // gefragt, denn eine Rückfrage, die immer kommt, wird blind
        // weggeklickt.
        onClick={eingerichtet ? () => setFragt(true) : eigenschaften.codeErzeugen}
        hinweis={
          sperrgrund ??
          (eingerichtet
            ? 'Bei neuem Handy oder vergessener PIN.'
            : 'Erzeugt einen Code zum Eintippen in der App.')
        }
      />

      {fragt && (
        <Rueckfrage
          titel={`App von ${person.vorname} ${person.nachname} neu einrichten?`}
          bestaetigungstext="Trotzdem neu einrichten"
          art="gefahr"
          laeuft={laeuft}
          // Wird die Person inzwischen gesperrt (Status in derselben Zeile),
          // ist auch „Trotzdem" gesperrt — die Datenbank lehnte ohnehin ab.
          bestaetigungGesperrt={sperrgrund !== null}
          abbrechen={() => setFragt(false)}
          bestaetigen={() => {
            setFragt(false);
            eigenschaften.codeErzeugen();
          }}
        >
          Auf einem Handy ist die App schon eingerichtet. Ein neuer Code ist nur
          nötig bei neuem Handy oder vergessener PIN. Wird er auf einem anderen
          Handy eingelöst, bleibt auf dem alten liegen, was dort noch nicht
          übertragen ist (Fahrten, Belege, Arbeitszeiten). Deshalb vorher: altes
          Handy mit Netz öffnen und warten, bis alles übertragen ist.
        </Rueckfrage>
      )}

      {person.offenerCode && (
        <KnopfMitHinweis
          art="still"
          text="Code ungültig machen"
          disabled={laeuft}
          onClick={eigenschaften.codeWeg}
          hinweis="Der offene Code gilt danach nicht mehr."
        />
      )}
    </div>
  );
}
